<template>
  <div class="tw-@container">
    <div v-if="$slots.label" class="tw-mb-2 tw-text-sm tw-font-semibold">
      <slot name="label" />
    </div>
    <div
      class="tw-grid tw-divide-neutral-200 tw-overflow-hidden tw-rounded-lg tw-border tw-border-neutral-200 tw-bg-white tw-shadow-sm"
      :class="
        isRightSidebarVisible
          ? '@lg:tw-grid-cols-[0.65fr_0.35fr] @lg:tw-divide-x'
          : 'tw-grid-cols-1'
      "
    >
      <div :class="{ 'tw-hidden @lg:tw-block': isRightSidebarVisible }">
        <FormInput
          v-model="searchInput"
          type="text"
          placeholder="Search..."
          class="tw-p-2"
        />

        <!--Groups on the left side-->
        <div v-if="!searchInput" class="tw-h-[250px] tw-overflow-y-auto tw-p-2">
          <div class="tw-flex tw-flex-col tw-space-y-1">
            <button
              v-for="group in groupWithButtonIds"
              :key="group.buttonId"
              :class="
                mergeClasses(
                  groupButtonClasses,
                  isGroupSelected(group) && 'tw-bg-neutral-50',
                )
              "
              @click="setSelectedGroup(group)"
              :id="group.buttonId"
              :test_id="group.name"
            >
              <span
                class="tw-inline-flex tw-items-center tw-space-x-2 tw-font-medium tw-text-neutral-600"
              >
                <span class="tw-text-sm">
                  {{ group.name }}
                </span>
                <span
                  :class="
                    mergeClasses(
                      'tw-hidden tw-h-6 tw-w-6 tw-place-items-center tw-rounded-full tw-bg-neutral-50 tw-text-xs tw-transition-colors group-hover:tw-bg-neutral-100 @sm:tw-grid',
                      isGroupSelected(group) && 'tw-bg-neutral-100',
                    )
                  "
                >
                  {{ group.options.length }}
                </span>
              </span>
              <ArrowRightIcon class="tw-h-3 tw-w-3 tw-text-neutral-600" />
            </button>
          </div>
        </div>
      </div>

      <!--Options on the right side-->
      <div v-if="isRightSidebarVisible" class="tw-h-[308px] tw-overflow-y-auto">
        <div class="tw-flex tw-flex-col tw-space-y-1 tw-p-2">
          <button
            @click="setSelectedGroup(null)"
            class="tw-inline-flex tw-items-center tw-justify-start tw-px-3 tw-py-2 tw-font-semibold @lg:tw-hidden"
          >
            <ArrowRightIcon class="tw-mr-2 tw-h-3 tw-w-3 tw-rotate-180" />
            Go back
          </button>
          <button
            v-for="option in selectedGroup.options"
            :key="option.value"
            :class="
              mergeClasses(
                optionButtonClasses,
                isOptionSelected(option) &&
                  'tw-border-secondary-500 hover:tw-border-secondary-500',
              )
            "
            @click="selectOption(option)"
            :test_id="option.label"
          >
            <span>
              {{ option.label }}
            </span>
            <CheckFullIcon
              v-if="isOptionSelected(option)"
              class="tw-h-4 tw-w-4 tw-text-secondary-500"
            />
          </button>
        </div>
      </div>

      <!--Results from search input-->
      <div v-if="searchInput" class="tw-h-[250px] tw-overflow-y-auto">
        <div class="tw-space-y-3 tw-p-3">
          <div
            class="tw-text-xs tw-font-medium tw-uppercase tw-text-neutral-400"
          >
            Results
          </div>
          <div
            v-if="foundOptionsWithGroups.length"
            class="tw-flex tw-flex-col tw-space-y-1"
          >
            <button
              v-for="(option, index) in foundOptionsWithGroups"
              :key="index"
              :class="
                mergeClasses(
                  optionButtonClasses,
                  isOptionSelected(option) &&
                    'tw-border-secondary-500 hover:tw-border-secondary-500',
                )
              "
              @click="selectOption(option)"
            >
              <span class="tw-inline-flex tw-items-center tw-space-x-2">
                <span>{{ option.group }}</span>
                <ArrowRight class="tw-h-2 tw-w-2" />
                <span>{{ option.label }}</span>
              </span>
              <CheckFullIcon
                v-if="isOptionSelected(option)"
                class="tw-h-4 tw-w-4 tw-text-secondary-500"
              />
            </button>
          </div>
          <div v-else class="tw-text-sm tw-text-neutral-500">
            Nothing found with "{{ searchInput }}"
          </div>

          <div
            class="tw-text-xs tw-font-medium tw-uppercase tw-text-neutral-400"
          >
            Groups
          </div>
          <div
            v-if="foundGroups.length"
            class="tw-flex tw-flex-col tw-space-y-1"
          >
            <button
              v-for="group in foundGroups"
              :key="group.name"
              :class="groupButtonClasses"
              @click="handleClickGroupFromSearchResults(group, group.buttonId)"
            >
              {{ group.name }}
            </button>
          </div>
          <div v-else class="tw-text-sm tw-text-neutral-500">
            Nothing found with "{{ searchInput }}"
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="isError"
      class="tw-mt-2 tw-text-xs tw-text-error-500"
      aria-live="assertive"
    >
      {{ Array.isArray(errors) ? errors.join(', ') : errors }}
    </div>

    <div v-if="!hideTags" class="tw-mt-4">
      <Tag
        v-for="selectedValue in selectedValuesWithLabels"
        :key="selectedValue.label"
        removable
        color="purple"
        class="tw-mb-1 tw-mr-1"
        @remove="selectOption(selectedValue)"
      >
        {{ selectedValue.label }}
      </Tag>
    </div>
  </div>
</template>

<script setup>
import FormInput from '@/shared/components/forms/FormInput.vue'
import ArrowRightIcon from '@/assets/images/icons/arrow-right.svg'
import { computed, nextTick, ref } from 'vue'
import { mergeClasses } from '@/shared/utils/mergeClasses.js'
import CheckFullIcon from '@/assets/images/icons/check-full.svg'
import Tag from '@/shared/components/Tag.vue'
import { matchSorter } from 'match-sorter'
import ArrowRight from '@/assets/images/icons/arrow-right.svg'
import { getUniqueId } from '@/shared/utils/uniqueId.js'

const emit = defineEmits(['update:modelValue'])

const props = defineProps({
  // [{ name: 'Group 1', options: [{ value: '1', label: 'Option 1' }] }]
  groups: {
    type: Array,
    required: true,
  },
  // ['1']
  modelValue: {
    type: [Array, Number, String],
    default: () => [],
  },
  multiple: {
    type: Boolean,
    default: false,
  },
  hideTags: {
    type: Boolean,
    default: false,
  },
  isError: {
    type: Boolean,
    default: false,
  },
  errors: {
    type: [Array, String],
    default: '',
  },
})

const isRightSidebarVisible = computed(
  () => selectedGroup.value && !searchInput.value,
)

const groupWithButtonIds = computed(() =>
  props.groups.map((group) => ({
    ...group,
    buttonId: `form-select-group-button-${getUniqueId()}`,
  })),
)

const groupButtonClasses =
  'tw-group tw-inline-flex tw-items-center tw-text-left tw-justify-between tw-rounded-lg tw-px-2 tw-py-1 tw-transition-colors hover:tw-bg-neutral-50'
const optionButtonClasses =
  'tw-group tw-inline-flex tw-items-center tw-justify-between tw-text-left tw-rounded-full tw-text-xs @sm:tw-text-sm tw-border tw-border-white tw-px-3 tw-py-2 tw-transition-colors hover:tw-border-neutral-200'

const selectedValuesWithLabels = computed(() => {
  const values = props.multiple ? props.modelValue : [props.modelValue]
  return values.map((selectedValue) => {
    const group = props.groups.find((group) => {
      return group.options.find((option) => option.value === selectedValue)
    })
    const option = group.options.find(
      (option) => option.value === selectedValue,
    )
    return {
      label: option.label,
      value: selectedValue,
    }
  })
})

const selectedGroup = ref(null)
const setSelectedGroup = (group) => {
  selectedGroup.value = group
}
const isGroupSelected = (group) => {
  return selectedGroup.value && selectedGroup.value.name === group.name
}
const handleClickGroupFromSearchResults = (group, buttonId) => {
  searchInput.value = ''
  selectedGroup.value = group
  if (!buttonId) return
  nextTick(() => {
    // Focusing the button makes it visible in the viewport
    document.getElementById(buttonId).focus()
  })
}

const isOptionSelected = (option) => {
  return props.multiple
    ? props.modelValue.includes(option.value)
    : props.modelValue === option.value
}
const selectOption = (option) => {
  if (props.multiple) {
    return isOptionSelected(option)
      ? emit(
          'update:modelValue',
          props.modelValue.filter((value) => value !== option.value),
        )
      : emit('update:modelValue', [...props.modelValue, option.value])
  }

  emit('update:modelValue', option.value)
}

const searchInput = ref('')
const foundOptionsWithGroups = computed(() => {
  const optionsWithGroups = props.groups.reduce((accumulator, currentGroup) => {
    return [
      ...accumulator,
      ...currentGroup.options.map((option) => ({
        group: currentGroup.name,
        label: option.label,
        value: option.value,
      })),
    ]
  }, [])
  return matchSorter(optionsWithGroups, searchInput.value, {
    keys: ['label'],
  })
})
const foundGroups = computed(() => {
  return matchSorter(groupWithButtonIds.value, searchInput.value, {
    keys: ['name'],
  })
})
</script>
